import type { MuscleGroup } from '@squats/shared';
import { useEffect, useRef, useState } from 'react';
import type { Figure3D as Figure, Figures3D } from '../figures/content.ts';

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const darkMode = () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

/**
 * The 3D exercise figure: a large three-quarter view you can drag to turn, with small front and side views
 * underneath. Muscles redden with effort as the rep plays; the main ones can be named.
 */
export function Figure3D({
  figure,
  lib,
  primary,
  secondary,
  label,
}: {
  figure: Figure;
  lib: Figures3D;
  primary: readonly MuscleGroup[];
  secondary: readonly MuscleGroup[];
  label: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const labelsRef = useRef<SVGSVGElement>(null);
  const [playing, setPlaying] = useState(() => !reducedMotion());
  const [names, setNames] = useState(true);
  const [effort, setEffort] = useState(true);
  const [failed, setFailed] = useState(false);
  // Read by the render loop without restarting it.
  const state = useRef({ playing, names, effort });
  state.current = { playing, names, effort };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let renderer: InstanceType<Figures3D['FigureRenderer']>;
    try {
      renderer = new lib.FigureRenderer(canvas, figure, primary, darkMode() ? lib.DARK_COLORS : lib.LIGHT_COLORS, secondary);
    } catch {
      setFailed(true);
      return;
    }
    const period = figure.period ?? 2800;
    // Paused figures (and reduced motion) show the hardest point of the rep.
    let ms = state.current.playing ? 0 : period / 2;
    let last = performance.now();
    let raf = 0;
    let visible = true;

    const tick = (now: number) => {
      const { playing: on, names: showNames, effort: showEffort } = state.current;
      if (on) ms += now - last;
      last = now;
      renderer.showEffort = showEffort;
      renderer.setTime(ms);
      renderer.render();
      drawLabels(labelsRef.current, showNames ? renderer.labels() : []);
      raf = visible ? requestAnimationFrame(tick) : 0;
    };
    raf = requestAnimationFrame(tick);

    // Stop drawing while scrolled out of view (browsers already pause it in background tabs).
    const io = new IntersectionObserver(([e]) => {
      visible = !!e?.isIntersecting;
      if (visible && !raf) {
        last = performance.now();
        raf = requestAnimationFrame(tick);
      }
    });
    io.observe(canvas);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      renderer.dispose();
    };
  }, [figure, lib, primary, secondary]);

  if (failed) {
    return <p className="muted small figure-missing">This device can’t show the 3D figure (WebGL is unavailable). The muscle map shows what it works.</p>;
  }

  return (
    <div className="figure3d">
      <div className="figure3d-stage">
        <canvas ref={canvasRef} role="img" aria-label={`3D animation: ${label}. Drag to turn it.`} />
        <svg ref={labelsRef} className="figure3d-labels" aria-hidden="true" />
        <span className="figure3d-view front" aria-hidden="true">
          Front
        </span>
        <span className="figure3d-view side" aria-hidden="true">
          Side
        </span>
      </div>
      <div className="row figure3d-controls">
        <button type="button" className="btn small" onClick={() => setPlaying((p) => !p)} aria-pressed={!playing}>
          {playing ? 'Pause' : 'Play'}
        </button>
        <span className="grow muted small">Drag to turn</span>
        <button type="button" className={`chip toggle${names ? ' on' : ''}`} onClick={() => setNames((n) => !n)} aria-pressed={names}>
          Names
        </button>
        <button type="button" className={`chip toggle${effort ? ' on' : ''}`} onClick={() => setEffort((e) => !e)} aria-pressed={effort}>
          Effort
        </button>
      </div>
    </div>
  );
}

const SVG = 'http://www.w3.org/2000/svg';

/** Main-muscle names with leader lines, stacked at the nearer side edge of the large view. */
function drawLabels(svg: SVGSVGElement | null, labels: { text: string; x: number; y: number }[]) {
  if (!svg) return;
  const w = svg.clientWidth;
  // Reuse the same elements each frame; add or remove only when the count changes.
  while (svg.childElementCount > labels.length) svg.lastElementChild!.remove();
  while (svg.childElementCount < labels.length) {
    const g = document.createElementNS(SVG, 'g');
    g.append(document.createElementNS(SVG, 'line'), document.createElementNS(SVG, 'circle'), document.createElementNS(SVG, 'text'));
    svg.append(g);
  }
  labels.forEach((l, i) => {
    const [line, dot, text] = svg.children[i]!.children as unknown as [SVGLineElement, SVGCircleElement, SVGTextElement];
    const left = l.x < w / 2;
    const tx = left ? 6 : w - 6;
    const ty = 18 + i * 18;
    setAttrs(line, { x1: l.x, y1: l.y, x2: left ? tx + 70 : tx - 70, y2: ty + 4 });
    setAttrs(dot, { cx: l.x, cy: l.y, r: 2.5 });
    setAttrs(text, { x: tx, y: ty, 'text-anchor': left ? 'start' : 'end' });
    if (text.textContent !== l.text) text.textContent = l.text;
  });
}

function setAttrs(el: Element, attrs: Record<string, string | number>) {
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
}
